import { useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * Um menu suspenso simples: um botão e um painel.
 *
 * Por que não um componente de biblioteca: a única coisa que falta aqui é **fechar
 * ao clicar fora** (o `select` que a casa já usa serve para lista de opções, e não
 * para painel com vários campos dentro). São vinte linhas, sem dependência nova — e
 * uma dependência nova, num pacote que roda no navegador de quem imprime, custa mais
 * do que isto.
 *
 * Quem usa passa o conteúdo do painel como filho: `menuAberto` decide se ele está na
 * tela, e `fechar` é chamado ao clicar fora ou apertar `Esc`.
 */
export function Menu({
  rotulo,
  children,
  largura = 'min(92vw, 30rem)',
  tom = 'principal',
}: {
  /** O texto do botão — é também o nome acessível dele. */
  rotulo: string;
  children: (acao: { fechar: () => void }) => ReactNode;
  /** A largura do painel (o conteúdo é que decide quanto ele precisa). */
  largura?: string;
  tom?: 'principal' | 'contorno';
}) {
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);
  const id = useId();

  /*
   * Fechar ao clicar fora: o `pointerdown` é do documento inteiro, e o que salva o
   * clique dentro do painel é a checagem de conter o alvo. Sem isto, o painel
   * fecharia ao clicar num campo dele mesmo — o defeito clássico de menu caseiro.
   */
  useEffect(() => {
    if (!aberto) return;
    const foraDoMenu = (evento: Event) => {
      const alvo = evento.target as HTMLElement | null;
      if (caixa.current?.contains(alvo as Node)) return;
      /*
       * A lista de um `select` do painel não é desenhada dentro dele: o Radix a
       * monta **no fim do corpo** da página, fora desta caixa. Sem esta segunda
       * checagem, o clique numa opção fechava o painel antes que a opção fosse
       * escolhida — o menu sumia e nada era aplicado, que é o defeito clássico de
       * menu com campo de lista dentro.
       */
      if (alvo?.closest?.('[data-radix-popper-content-wrapper]')) return;
      setAberto(false);
    };
    const tecla = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setAberto(false);
    };
    document.addEventListener('pointerdown', foraDoMenu);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('pointerdown', foraDoMenu);
      document.removeEventListener('keydown', tecla);
    };
  }, [aberto]);

  return (
    <div ref={caixa} className="relative">
      <button
        type="button"
        aria-expanded={aberto}
        aria-controls={id}
        onClick={() => setAberto((atual) => !atual)}
        className={[
          'inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors',
          tom === 'principal'
            ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
            : 'border-input bg-background hover:bg-muted',
        ].join(' ')}
      >
        {rotulo}
        <ChevronDown className={['size-4 transition-transform', aberto ? 'rotate-180' : ''].join(' ')} />
      </button>

      {aberto ? (
        <div
          id={id}
          role="group"
          aria-label={rotulo}
          style={{ width: largura }}
          className="absolute right-0 top-full z-40 mt-1 max-h-[72vh] overflow-auto rounded-lg border border-border bg-card p-3 shadow-lg"
        >
          {children({ fechar: () => setAberto(false) })}
        </div>
      ) : null}
    </div>
  );
}

/** Um grupo de campos dentro do painel do menu, com título. */
export function GrupoDoMenu({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="space-y-3 border-b border-border py-3 first:pt-0 last:border-b-0 last:pb-0">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{titulo}</h3>
      {children}
    </section>
  );
}
